import React from 'react';
import { Box } from '@10d/tend-ui-grid';
import { Text, Title } from '@10d/tend-ui-typography';
import { ArrowBack } from '@10d/tend-ui-icons/ArrowBack';
import { ChevronRight } from '@10d/tend-ui-icons/ChevronRight';
import { UNSTABLE_useControllableStateV2 } from '@10d/tend-ui-hooks';
import { Counter } from '../Counter/Counter.js';
import { Dot } from '../Dot/Dot.js';
import { Drawer } from '../Drawer/Drawer.js';
import { isDividerStackNavigationItemType, isGroupStackNavigationItemType, isSubStackNavigationItemType, isStackNavigationItemType } from './utils.js';
import { NavigationItem } from './components/NavigationItem/NavigationItem.js';
import { NavigationGroupItem } from './components/NavigationGroupItem/NavigationGroupItem.js';
import { NavigationList } from './components/NavigationList/NavigationList.js';
import { Root } from './styled.js';

const Badge = ({ badge }) => {
    switch (badge === null || badge === void 0 ? void 0 : badge.type) {
        case 'counter':
            return React.createElement(Counter, Object.assign({ preset: 'blue' }, badge));
        case 'dot':
            return React.createElement(Dot, Object.assign({ preset: 'blue' }, badge));
        default:
            return null;
    }
};
const StackNavigation = ({ items, defaultSelectedKeys, selectedKeys, onSelect, }) => {
    const [stack, setStack] = React.useState([]);
    const [__selectedKeys = [], __setSelectedKeys] = UNSTABLE_useControllableStateV2({
        value: selectedKeys,
        defaultValue: defaultSelectedKeys,
        onChange: onSelect,
    });
    const keys = stack.map(value => value.key).filter(Boolean);
    const push = React.useCallback((item) => {
        setStack(prevStack => [...prevStack, item]);
    }, []);
    const pop = React.useCallback(() => {
        setStack(prevStack => prevStack.slice(0, -1));
    }, []);
    const createHandler = React.useCallback((item) => {
        return (domEvent) => {
            var _a;
            if (!isDividerStackNavigationItemType(item)) {
                (_a = item === null || item === void 0 ? void 0 : item.onClick) === null || _a === void 0 ? void 0 : _a.call(item, { domEvent });
            }
            const hasChildren = 'children' in item;
            if (!hasChildren) {
                const add = [item.key];
                /**
                 * С цель сохранить совместимость интерфейсов Menu из antd (Menu в antd собирает путь в реверсном порядке)
                 * собирать путь нужно в обратном порядке
                 */
                const path = stack
                    .slice(0)
                    .reverse()
                    .map(v => v.key);
                __setSelectedKeys(add.concat(path));
                setStack([]);
                return;
            }
            push(item);
        };
    }, [push, __setSelectedKeys, stack]);
    const isSelected = React.useCallback((key = '') => __selectedKeys.includes(key), [__selectedKeys]);
    const last = stack[stack.length - 1];
    const title = last && !isDividerStackNavigationItemType(last) && (last === null || last === void 0 ? void 0 : last.label);
    /**
     * Из-за сложной логики вложенности навигации
     * приходится проверять прошлого и следующего предка
     * чтобы понимать какое закругление нужно давать элементу
     */
    const borderRadius = React.useCallback((previousSibling, nextSibling) => {
        const [, borderRadius] = [
            [!previousSibling && !nextSibling, '16px'],
            [!previousSibling, '16px 16px 0 0'],
            [!previousSibling, '16px 16px 0 0'],
            [!nextSibling, '0 0 16px 16px'],
            [
                previousSibling && isGroupStackNavigationItemType(previousSibling),
                '16px 16px 0 0',
            ],
            [nextSibling && isGroupStackNavigationItemType(nextSibling), '0 0 16px 16px'],
        ].filter(([condition]) => !!condition)[0] || [];
        return borderRadius;
    }, []);
    const render = React.useCallback((item, previousSibling, nextSibling) => {
        if (isGroupStackNavigationItemType(item)) {
            return (React.createElement(React.Fragment, { key: item.key },
                React.createElement(NavigationGroupItem, null,
                    React.createElement(Text, { size: 'small', color: 'gray500' }, item.label),
                    item.children && (React.createElement(NavigationList, { style: { marginTop: '8px' } }, item.children.map((v, index, self) => render(v, self[index - 1], self[index + 1])))))));
        }
        if (isSubStackNavigationItemType(item))
            return (React.createElement(React.Fragment, { key: item.key },
                React.createElement(NavigationItem, { onClick: createHandler(item), borderRadius: borderRadius(previousSibling, nextSibling) },
                    React.createElement(Box, { className: 'tend-ui-stack-navigation-item-content', "$display": 'flex', "$alignItems": 'center', "$gap": 8, "$width": '100%' },
                        item.icon,
                        React.createElement(Text, { color: isSelected(item.key) ? 'blue600' : undefined, style: { flex: '1' } }, item.label),
                        item.badge && React.createElement(Badge, { badge: item.badge }),
                        React.createElement(ChevronRight, { size: 24, color: 'gray200' }))),
                item.children && (React.createElement(Drawer.Root, { fullscreen: { offset: '0px' }, backgroundColor: 'gray50', open: keys.includes(item.key || '') },
                    React.createElement(Drawer.Header, null,
                        React.createElement(Box, { "$display": 'flex', "$alignItems": 'center', "$gap": 8, "$width": '100%' },
                            React.createElement(ArrowBack, { onClick: pop, size: 24 }),
                            React.createElement(Title, { margin: 0, level: 'h6' }, title))),
                    React.createElement(Drawer.Body, null,
                        React.createElement(NavigationList, null, item.children.map((v, index, self) => render(v, self[index - 1], self[index + 1]))))))));
        if (isStackNavigationItemType(item)) {
            return (React.createElement(NavigationItem, { key: item.key, borderRadius: borderRadius(previousSibling, nextSibling), onClick: createHandler(item) },
                React.createElement(Box, { className: 'tend-ui-stack-navigation-item-content', "$display": 'flex', "$alignItems": 'center', "$gap": 8, "$width": '100%' },
                    item.icon,
                    React.createElement(Text, { color: isSelected(item.key) ? 'blue600' : undefined, style: { flex: '1' } }, item.label),
                    item.badge && React.createElement(Badge, { badge: item.badge }))));
        }
        return null;
    }, [createHandler, isSelected, keys, pop, title, borderRadius]);
    return (React.createElement(Root, { className: 'tend-ui-stack-navigation-root' }, (items || []).map((item, index, self) => render(item, self[index - 1], self[index + 1]))));
};
StackNavigation.displayName = 'StackNavigation';

export { StackNavigation };
